import React, { useEffect, useState, ChangeEvent, useCallback, useRef } from 'react'
import { apiURI } from 'src/lib/utils/constants'
import { postRequest, getRequest, patchRequest } from 'src/lib/core/api-client'
import { useRouter } from 'next/router'
import { Typography, Grid, Checkbox } from '@mui/material'

import LayoutContainer from 'src/components/common/containers/LayoutContainer'
import InputTextField from 'src/components/common/form-elements/InputTextField'
import SaveNotification from 'src/components/common/SaveNotification'
import { useUser } from 'src/lib/contexts/UserContext'
import Alerts from 'src/components/common/feedback-and-dialog/Alerts'
import { useLoader } from 'src/lib/contexts/LoaderContext'
import PermissionAccordion from './PermissionAccordion'
import {
  buildTree,
  formatData,
  getSelectedPermissions,
  selectAllPermissions,
  updatePermissionTree
} from 'src/lib/utils/newEditRoleHelpers'
import { useActivityLogs, EntityName } from 'src/lib/hooks/useActivityLogs'

interface NewEditRoleProps {
  roleId?: string
}

interface Permission {
  id: string
  label: string
  isSelected: boolean
  weight: number
  visible: boolean
  parent_permission_id: string | null
  rolesAllowed: Permission[]
}

let initialPermissionsState: Permission[]

const NewEditRole: React.FC<NewEditRoleProps> = ({ roleId }) => {
  const { sendActivityLog } = useActivityLogs()

  const [permissions, setPermissions] = useState<Permission[]>([])
  const [roleName, setRoleName] = useState<string>('')
  const [showSaveStrip, setShowSaveStrip] = useState<boolean>(false)
  const [initialRoleName, setInitialRoleName] = useState<string>('')
  const [openAlert, setOpenAlert] = useState<string>('')
  const checkboxRef = useRef<HTMLInputElement>(null)

  const { user } = useUser()
  const router = useRouter()
  const { setLoaderProperties } = useLoader()

  useEffect(() => {
    onInit()
  }, [])

  useEffect(() => {
    if (!permissions || !initialPermissionsState) return
    setShowSaveStrip(JSON.stringify(permissions) !== JSON.stringify(initialPermissionsState))
  }, [permissions])

  const onInit = async () => {
    const promises = [getRequest(`/merchant-account/permissions`, apiURI.AUTH_ENGINE)]
    if (Boolean(roleId)) {
      promises.push(getRequest(`/merchant-account/roles/${roleId}`, apiURI.AUTH_ENGINE))
    }
    try {
      const responses = await Promise.allSettled(promises)
      const permissionsList: Permission[] = []
      let tree: Permission[] = []
      let editRolePermissionsList: any = []
      let roleName = ''
      if (responses[1]?.status === 'fulfilled') {
        const editRoleResponse: any = responses[1].value
        const { data } = editRoleResponse
        editRolePermissionsList = [...data?.data?.permissions]
        roleName = data?.data?.name
      }
      if (responses[0].status === 'fulfilled') {
        const rolesResponse: any = responses[0].value
        const { data } = rolesResponse

        data?.data?.forEach((permission: any) => {
          if (permission.name.startsWith('TAB')) {
            const isSelected = editRolePermissionsList.some((value: any) => value.name === permission.name)
            const value: Permission = {
              id: permission.name,
              label: permission.display_name,
              isSelected: Boolean(isSelected),
              weight: permission.weight,
              visible: permission.visible,
              parent_permission_id: permission.parent_permssion_id,
              rolesAllowed: []
            }
            permissionsList.push(value)
          }
        })
        const allPermissions: Permission[] = formatData(data.data, editRolePermissionsList)
        tree = buildTree(permissionsList, allPermissions)
      }
      setPermissions(tree)
      setRoleName(roleName)
      initialPermissionsState = tree
      setInitialRoleName(roleName)
    } catch (e) {
      console.error(e)
    }
  }

  useEffect(() => {
    setShowSaveStrip(roleName !== initialRoleName)
  }, [roleName, initialRoleName])

  const isAllSelected = permissions.length > 0 && permissions.every(permission => permission.isSelected)
  const isSomeSelected = permissions.some(permission => permission.isSelected)
  const isAllSelectedIndeterminate = !isAllSelected && isSomeSelected

  useEffect(() => {
    if (checkboxRef.current) {
      checkboxRef.current.indeterminate = isAllSelectedIndeterminate
    }
  }, [isAllSelectedIndeterminate])

  const handleSelectAllPermissions = (event: ChangeEvent<HTMLInputElement>) => {
    if (!permissions) return
    const updatedPermissions = selectAllPermissions(event, permissions)

    setPermissions(updatedPermissions)
  }

  const handlePermissionToggle = (event: any, targetId: string) => {
    if (!event || !targetId || !permissions) return

    const updatedPermissions = updatePermissionTree(event, targetId, permissions)
    setPermissions(updatedPermissions)
  }

  const handleSaveRole = async () => {
    setLoaderProperties({ isLoaderVisible: true })
    if (!Boolean(roleName)) {
      setOpenAlert('roleName_missing')
      setLoaderProperties({ isLoaderVisible: false })
      return
    }
    const permissionsArray: any = getSelectedPermissions(permissions as Permission[])

    if (!Boolean(permissionsArray?.length)) {
      setOpenAlert('permissions_missing')
      setLoaderProperties({ isLoaderVisible: false })
      return
    }

    const payload = {
      name: roleName,
      description: '',
      permissions: permissionsArray,
      created_by: user?.email
    }

    try {
      let response: any
      if (Boolean(roleId)) {
        response = await patchRequest(`/merchant-account/roles/${roleId}`, apiURI.AUTH_ENGINE, payload)
      } else {
        response = await postRequest('/merchant-account/roles', apiURI.AUTH_ENGINE, payload)
      }
      if (response?.error) {
        setOpenAlert(response?.error)
        return
      }
      const activityLogPayload = {
        entityName: EntityName.SETTINGS,
        actionName: 'edited Accounts'
      }
      sendActivityLog(activityLogPayload)
      setOpenAlert('success')
      setShowSaveStrip(false)
      setTimeout(() => router.push('/settings/members'), 500)
    } catch (e: any) {
      console.error(e)
      setOpenAlert(e?.error ?? 'Something went wrong!')
    }
    setLoaderProperties({ isLoaderVisible: false })
  }

  const handleDiscardRole = () => {
    setPermissions(initialPermissionsState)
    setRoleName(initialRoleName)
  }

  const handleCloseAlert = (event: any, reason: any) => {
    if (reason === 'clickaway') {
      return
    }
    setOpenAlert('')
  }

  const getAlertMsg = useCallback(() => {
    if (openAlert === 'success') return 'Role created successfully!'
    if (openAlert === 'permissions_missing') return 'Please select atlease one role.'
    if (openAlert === 'roleName_missing') return 'Please enter a role name.'
    return openAlert
  }, [openAlert])

  return (
    <>
      <div className='flex flex-col gap-4 xl:w-3/5'>
        <LayoutContainer>
          <Grid item xs={12} md={12} display={'flex'} gap={6} justifyContent={'start'} alignItems={'center'}>
            <InputTextField
              errorStatus={false}
              textFieldId='role-name'
              label={'Permission name'}
              inputProps={{ 'aria-label': 'controlled' }}
              inputValue={roleName}
              handleChange={(event: ChangeEvent<HTMLInputElement>) => {
                const value = event?.target?.value
                setRoleName(value)
              }}
              inputPlaceholder={'Enter a role name'}
            />
          </Grid>
        </LayoutContainer>

        <LayoutContainer>
          <div className='-m-4'>
            <Typography variant='body1' sx={{ fontWeight: 600, margin: 4, marginBottom: 2 }}>
              Permissions
            </Typography>
            <div className='flex items-center px-4 py-2 my-2 cursor-pointer'>
              <input
                id='selectAll'
                type='checkbox'
                ref={checkboxRef}
                checked={isAllSelectedIndeterminate || isAllSelected}
                onChange={handleSelectAllPermissions}
                className='mr-3 w-4 h-4 accent-[#4D4D4D]'
              />
              <label htmlFor='selectAll' className='text-base font-medium'>
                Select All Permissions
              </label>
            </div>
            {permissions?.map((perm, index) => (
              <div
                key={perm.id}
                style={{
                  borderTop: index === 0 ? '1px solid #ddd' : 'none', // Top border only for the first accordion
                  borderBottom: index === permissions.length - 1 ? 'none' : '1px solid #ddd' // Bottom border only for the last accordion
                }}
              >
                <PermissionAccordion permission={perm} handlePermToggle={handlePermissionToggle} />
              </div>
            ))}
          </div>
        </LayoutContainer>
      </div>
      {openAlert.length ? (
        <Alerts
          open={true}
          duration={3000}
          closeHandler={handleCloseAlert}
          severity={openAlert === 'success' ? 'success' : 'error'}
          message={getAlertMsg()}
        />
      ) : null}
      <SaveNotification onSave={handleSaveRole} onDiscard={handleDiscardRole} isVisible={showSaveStrip} />
    </>
  )
}

export default NewEditRole
